import React from 'react';
import { useQuery } from 'react-query';
import { FiUsers, FiBookOpen, FiUserCheck, FiDollarSign, FiActivity } from 'react-icons/fi';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { useAcademicYearQuery } from '../hooks/useAcademicYearQuery';
import MobileCard from '../components/common/MobileCard';
import MobileCardList from '../components/common/MobileCardList';

const Dashboard = () => {
  const { selectedYear } = useAcademicYear();
  const { yearParams } = useAcademicYearQuery('dashboard');
  
  const { data: dashboardData, isLoading } = useQuery(
    ['dashboard', selectedYear?.id],
    () => api.get('/dashboard', { params: yearParams }).then(res => res.data.data),
    {
      refetchInterval: 60000, // Refresh every minute
      enabled: !!selectedYear,
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Élèves',
      value: dashboardData?.stats?.total_eleves || 0,
      icon: FiUsers,
      color: 'bg-blue-500',
    },
    {
      name: 'Classes Actives',
      value: dashboardData?.stats?.classes_actives || 0,
      icon: FiBookOpen,
      color: 'bg-green-500',
    },
    {
      name: 'Inscriptions',
      value: dashboardData?.stats?.total_inscriptions || 0,
      icon: FiUserCheck,
      color: 'bg-purple-500',
    },
    {
      name: 'Paiements du mois',
      value: `${(dashboardData?.stats?.paiements_mois || 0).toLocaleString('fr-FR')} FCFA`,
      icon: FiDollarSign,
      color: 'bg-yellow-500',
    },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'inscription':
        return <FiUserCheck className="h-5 w-5 text-green-500" />;
      case 'paiement':
        return <FiDollarSign className="h-5 w-5 text-blue-500" />;
      default:
        return <FiActivity className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div>
      <div className="mb-4 sm:mb-6">
        <h1 className="page-title">Tableau de bord</h1>
        <p className="text-sm text-gray-600">
          Année scolaire: {dashboardData?.annee_scolaire?.annee_scolaire}
        </p>
      </div>
      
      {/* Stats — grille 2 colonnes compacte sur mobile */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="stat-mini-card">
            <div className="flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-center gap-2 sm:gap-0">
              <div className={`${stat.color} rounded-lg p-2.5 sm:p-3 sm:mr-4`}>
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="sm:ml-0 min-w-0">
                <p className="text-[11px] sm:text-sm font-medium text-gray-600 leading-tight">{stat.name}</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900 truncate">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Activité récente</h2>
          </div>
          <div className="p-6">
            {dashboardData?.activities?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.activities.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    {getActivityIcon(activity.type)}
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500">
                        {activity.action} - {format(new Date(activity.date), 'dd MMM yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Aucune activité récente
              </p>
            )}
          </div>
        </div>

        {/* Stats by Class */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Statistiques par classe</h2>
          </div>
          <div className="p-6">
            {dashboardData?.stats_par_classe?.length > 0 ? (
              <>
                <MobileCardList className="md:hidden">
                  {dashboardData.stats_par_classe.map((classe, index) => (
                    <MobileCard
                      key={index}
                      title={classe.classe}
                      fields={[
                        { label: 'Total', value: classe.nb_eleves },
                        { label: 'Garçons', value: classe.nb_garcons },
                        { label: 'Filles', value: classe.nb_filles },
                      ]}
                    />
                  ))}
                </MobileCardList>
                <div className="hidden md:block space-y-3">
                  {dashboardData.stats_par_classe.map((classe, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{classe.classe}</span>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">Total: {classe.nb_eleves}</span>
                        <span className="text-sm text-blue-600">G: {classe.nb_garcons}</span>
                        <span className="text-sm text-pink-600">F: {classe.nb_filles}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Aucune donnée disponible
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;